import { Ban, Megaphone, RotateCcw, Send, Settings, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ErrorState } from '../../../../components/ErrorState';
import { Menu } from '../../../../components/Menu';
import { AuditTrailList } from '../../../../components/index-page/AuditTrailList';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { RichTextDocument } from '../../../../components/RichTextDocument';
import { attachmentIdsIn, useAttachmentImages } from '../../../../hooks/useAttachmentImages';
import { Skeleton } from '../../../../components/Skeleton';
import { StatusText } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime } from '../../../../lib/formatDate';
import { AttachmentsBox } from '../components/AttachmentsBox';
import { PostOperationReportsPanel } from '../components/PostOperationReportsPanel';
import { PostingsCard } from '../components/PostingsCard';
import { PressReleaseModal } from '../components/PressReleaseModal';
import { pressReleasesApi } from '../pressReleaseApi';
import {
  PRESS_RELEASE_STATUS,
  PRESS_RELEASES_PATH,
  postingsLock,
  pressReleaseLock,
  type PressReleaseAttachmentItem,
  type PressReleaseItem,
} from '../pressReleaseTypes';

type TabKey = 'info' | 'content' | 'reports' | 'audit';

/**
 * One press release — its details on Info; the document and, beneath it, its files as a box of
 * thumbnails on Content; and every write ever made to it on Audit trail.
 *
 * **It LANDS on Content** — a press release is opened to be READ. Info is still the first tab declared.
 *
 * **Each card edits only itself** (Details the title and date, Content the document), and both are
 * disabled with the server's own reason once the release is published or deleted.
 *
 * **The banner owns the lifecycle**: Publish on a draft (it WRITES, so it confirms — and says the
 * release locks), Restore on a deleted one (the undo — confirmed, no password).
 */
export function PressReleaseProfilePage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();

  const [release, setRelease] = useState<PressReleaseItem | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [tab, setTab] = useState<TabKey>('content');
  const [editing, setEditing] = useState<'details' | 'content' | null>(null);
  const [pending, setPending] = useState<'publish' | 'restore' | 'delete' | null>(null);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  // A different release drops what is on screen first.
  const [shownId, setShownId] = useState(id);
  if (shownId !== id) {
    setShownId(id);
    setRelease(null);
    setError(null);
  }

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      pressReleasesApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;
          setRelease(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this press release.', 0));
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /*
    The pictures the document places in its own text, resolved BEFORE any early return — a hook
    cannot sit beside `lock`, which is computed after the loading and not-found branches.

    The attachment ROWS are fetched because they carry the type the SERVER verified on upload,
    and that is what decides whether a thing is drawn at all. Reading the type off the download
    response instead would be trusting a header to say what the bytes are, which is the rule
    `useBlobPreview` exists to keep.
  */
  const [bodyPictures, setBodyPictures] = useState<PressReleaseAttachmentItem[]>([]);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    void pressReleasesApi
      .attachments(id, { page: 1, pageSize: 100 })
      .then((page) => {
        if (!cancelled) setBodyPictures(page.items.filter((a) => a.isActive));
      })
      // A failed list draws the text and empty frames; the document is still readable.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [id, release?.body]);

  const bodyImageIds = attachmentIdsIn(release?.body);

  const loadBodyImage = useCallback(
    async (attachmentId: string) => {
      const downloaded = await pressReleasesApi.download(id!, attachmentId);
      const match = bodyPictures.find((attachment) => attachment.id === attachmentId);

      return { blob: downloaded.blob, contentType: match?.contentType ?? null };
    },
    [id, bodyPictures],
  );

  const bodyImageUrls = useAttachmentImages(id ? bodyImageIds : [], loadBodyImage);

  async function runPending() {
    if (!release || !pending) return;

    setRunning(true);
    setActionError(null);

    try {
      if (pending === 'publish') await pressReleasesApi.publish(release.id);
      else if (pending === 'delete') await pressReleasesApi.remove(release.id);
      else await pressReleasesApi.restore(release.id);

      toast.success(`${pending === 'publish' ? 'Published' : pending === 'delete' ? 'Deleted' : 'Restored'} "${release.title}".`);
      setPending(null);
      load();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Public Relations',
    { label: 'Press Release', to: PRESS_RELEASES_PATH },
    release?.title ?? 'Press release',
  ];

  if (error && !release) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} onRetry={() => load()} />
      </div>
    );
  }

  if (!release) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-4 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full max-w-4xl" />
        </div>
      </div>
    );
  }

  const lock = pressReleaseLock(release);
  const published = release.status === PRESS_RELEASE_STATUS.published;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell<TabKey>
        id="press-release-profile"
        panel={tab === 'audit' ? 'bleed' : 'cards'}
        header={
          <ProfileHeader
            id="press-release-profile-header"
            icon={<Megaphone className="h-6 w-6" aria-hidden="true" />}
            title={release.title}
            meta={[formatDateOnly(release.releaseDate)]}
            status={
              !release.isActive ? (
                <StatusText tone="inactive">Deleted</StatusText>
              ) : published ? (
                <StatusText tone="active">{release.statusLabel}</StatusText>
              ) : (
                <StatusText tone="pending">{release.statusLabel}</StatusText>
              )
            }
            createdBy={release.createdByName}
            createdAt={release.createdAt}
            actions={
              <Menu
                id="press-release-profile-settings"
                items={[
                  {
                    key: 'publish',
                    label: 'Publish',
                    icon: <Send className="h-4 w-4" aria-hidden="true" />,
                    permission: 'press-releases.publish',
                    disabledReason: lock,
                    onClick: () => setPending('publish'),
                  },
                  release.isActive
                    ? {
                        key: 'delete',
                        label: 'Delete',
                        icon: <Trash2 className="h-4 w-4" aria-hidden="true" />,
                        permission: 'press-releases.delete',
                        destructive: true,
                        divider: true,
                        onClick: () => setPending('delete'),
                      }
                    : {
                        key: 'restore',
                        label: 'Restore',
                        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
                        permission: 'press-releases.delete',
                        divider: true,
                        onClick: () => setPending('restore'),
                      },
                ]}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    id="press-release-profile-settings-trigger"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Press release settings"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          !release.isActive ? (
            <ProfileBanner
              id="press-release-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This press release is deleted. Its content and files are kept, and it can be restored."
              action={
                <PermissionGate permission="press-releases.delete">
                  <Button
                    id="press-release-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => setPending('restore')}
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          ) : published ? (
            <ProfileBanner
              id="press-release-profile-banner"
              icon={<Send className="h-4 w-4" aria-hidden="true" />}
              message={
                release.publishedAt != null
                  ? `Published on ${formatDateTime(release.publishedAt)}. It can no longer be edited, and no file can be added or removed.`
                  : 'Published. It can no longer be edited, and no file can be added or removed.'
              }
            />
          ) : (
            <ProfileBanner
              id="press-release-profile-banner"
              icon={<Megaphone className="h-4 w-4" aria-hidden="true" />}
              message={`A draft, for release on ${formatDateOnly(release.releaseDate)}. Publish it when it is ready to go out.`}
              action={
                <PermissionGate permission="press-releases.publish">
                  <Button
                    id="press-release-profile-publish"
                    variant="secondary"
                    leading={<Send className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => setPending('publish')}
                  >
                    Publish
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[
          { key: 'info', label: 'Info' },
          { key: 'content', label: 'Content' },
          /*
            Only on a release that references a case, and on the key its content needs — the case's
            own membership and level then apply inside, exactly as on the case.
          */
          ...(release.caseId
            ? [{ key: 'reports' as const, label: 'Post Operation Report', permission: 'post-operation-reports.read' }]
            : []),
          { key: 'audit', label: 'Audit trail', permission: 'audit-logs.read' },
        ]}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === 'info' ? (
          <>
            <ProfileCard
              id="press-release-profile-details"
              title="Details"
              onEdit={() => setEditing('details')}
              editPermission="press-releases.update"
              editDisabledReason={lock}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Title', value: release.title },
                  { label: 'Release date', value: formatDateOnly(release.releaseDate), mono: true },
                  { label: 'Status', value: release.statusLabel },
                  { label: 'Published', value: release.publishedAt != null ? formatDateTime(release.publishedAt) : null, mono: true },
                ]}
              />
            </ProfileCard>

            <PostingsCard releaseId={release.id} releaseTitle={release.title} lockReason={postingsLock(release)} />

            <ProfileCard id="press-release-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: release.createdByName },
                  { label: 'Created', value: formatDateTime(release.createdAt), mono: true },
                  { label: 'Updated by', value: release.updatedByName },
                  { label: 'Updated', value: release.updatedAt != null ? formatDateTime(release.updatedAt) : null, mono: true },
                ]}
              />
            </ProfileCard>
          </>
        ) : tab === 'content' ? (
          <>
            <ProfileCard
              id="press-release-profile-content"
              title="Content"
              onEdit={() => setEditing('content')}
              editPermission="press-releases.update"
              editDisabledReason={lock}
              padded
            >
              {/*
                The document's own pictures, fetched through the authenticated client. The
                stored markup carries no src — see RichText.Policy.WithAttachedImages — so a
                picture is drawn from a resolved object URL or not at all.
              */}
              <RichTextDocument html={release.body ?? null} imageUrls={bodyImageUrls} />
            </ProfileCard>

            {/* The files BELOW the content, in the same tab. */}
            <AttachmentsBox releaseId={release.id} releaseTitle={release.title} lockReason={lock} onChanged={() => load()} />
          </>
        ) : tab === 'reports' && release.caseId ? (
          <PostOperationReportsPanel caseId={release.caseId} />
        ) : (
          <div className="flex h-full min-h-0 flex-col p-4">
            <AuditTrailList module="press-releases" entityId={release.id} emptyMessage="No recorded activity for this press release yet." />
          </div>
        )}
      </ProfileHeaderShell>

      {editing !== null ? (
        <PressReleaseModal
          editing={release}
          section={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      ) : null}

      {pending ? (
        <ConfirmDialog
          open
          onClose={() => {
            setPending(null);
            setActionError(null);
          }}
          onConfirm={() => void runPending()}
          title={pending === 'publish' ? 'Publish press release' : pending === 'delete' ? 'Delete press release' : 'Restore press release'}
          danger={pending === 'delete'}
          requireConfirm
          requireCredential={pending === 'delete'}
          loading={running}
          id={`press-release-profile-${pending}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {pending === 'publish'
                  ? `"${release.title}" is published. After this it can no longer be edited, and no file can be added or removed.`
                  : pending === 'delete'
                    ? `"${release.title}" leaves the register. Nothing is erased — its content and files are kept, and it can be restored.`
                    : `"${release.title}" returns to the register, with its files.`}
              </p>

              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}
